import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/lib/api/client';

export interface BrandingSettings {
  login_logo_left: string | null;
  login_logo_right: string | null;
  admin_logo: string | null;
  favicon: string | null;
  meta_title: string | null;
  meta_description: string | null;
}

export const DEFAULT_SETTINGS: BrandingSettings = {
  login_logo_left: null,
  login_logo_right: null,
  admin_logo: null,
  favicon: 'https://admin.lilmiracles.in/assets/images/logo-sm.png',
  meta_title: 'Lil Miracles Admin',
  meta_description: 'Lil Miracles Admin Dashboard — Preschool Management System',
};

interface SettingsContextType {
  settings: BrandingSettings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
  updateSettings: (updates: Partial<BrandingSettings>) => Promise<BrandingSettings>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

/**
 * Update the document favicon in real-time.
 */
function applyFaviconToDOM(faviconUrl: string) {
  if (!faviconUrl) return;

  // Try finding existing icon link elements
  const existingIcons = document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']");
  if (existingIcons.length > 0) {
    existingIcons.forEach((link) => {
      link.href = faviconUrl;
    });
  } else {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.type = 'image/png';
    newLink.href = faviconUrl;
    document.head.appendChild(newLink);
  }
}

/**
 * Update document title in real-time.
 */
function applyMetaTitleToDOM(title: string) {
  if (!title) return;
  document.title = title;

  let ogTitle = document.querySelector<HTMLMetaElement>("meta[property='og:title']");
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  ogTitle.content = title;
}

/**
 * Update meta description in real-time.
 */
function applyMetaDescriptionToDOM(description: string) {
  if (!description) return;

  let metaDesc = document.querySelector<HTMLMetaElement>("meta[name='description']");
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = description;

  let ogDesc = document.querySelector<HTMLMetaElement>("meta[property='og:description']");
  if (!ogDesc) {
    ogDesc = document.createElement('meta');
    ogDesc.setAttribute('property', 'og:description');
    document.head.appendChild(ogDesc);
  }
  ogDesc.content = description;
}

/**
 * Apply all runtime branding & metadata to DOM.
 */
export function applySettingsToDOM(settings: BrandingSettings) {
  if (settings.meta_title) {
    applyMetaTitleToDOM(settings.meta_title);
  }
  if (settings.meta_description) {
    applyMetaDescriptionToDOM(settings.meta_description);
  }
  if (settings.favicon) {
    applyFaviconToDOM(settings.favicon);
  }
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<BrandingSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchSettings = useCallback(async () => {
    try {
      // Use the public endpoint so it works both when logged in and on the login page
      const response = await apiClient.get('/admin/settings/public');
      const data = response.data?.data || response.data;
      if (data) {
        const merged: BrandingSettings = {
          login_logo_left: data.login_logo_left ?? null,
          login_logo_right: data.login_logo_right ?? null,
          admin_logo: data.admin_logo ?? null,
          favicon: data.favicon || DEFAULT_SETTINGS.favicon,
          meta_title: data.meta_title || DEFAULT_SETTINGS.meta_title,
          meta_description: data.meta_description || DEFAULT_SETTINGS.meta_description,
        };
        setSettings(merged);
        applySettingsToDOM(merged);
      }
    } catch (err) {
      console.warn('Could not load branding settings, using defaults', err);
      applySettingsToDOM(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (updates: Partial<BrandingSettings>): Promise<BrandingSettings> => {
    const response = await apiClient.put('/admin/settings', updates);
    const updatedData = response.data?.data || response.data;
    const merged: BrandingSettings = {
      login_logo_left: updatedData.login_logo_left ?? null,
      login_logo_right: updatedData.login_logo_right ?? null,
      admin_logo: updatedData.admin_logo ?? null,
      favicon: updatedData.favicon || DEFAULT_SETTINGS.favicon,
      meta_title: updatedData.meta_title || DEFAULT_SETTINGS.meta_title,
      meta_description: updatedData.meta_description || DEFAULT_SETTINGS.meta_description,
    };

    setSettings(merged);
    applySettingsToDOM(merged);
    return merged;
  };

  return (
    <SettingsContext.Provider value={{ settings, isLoading, refreshSettings: fetchSettings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
